import { cn } from "@/lib/utils";
import { clampToMax, clampToRange } from "./indicatorMath";

const NAVY_FILL = "#272C57";
const TRACK_COLOR = "#EEE9E1";
const TARGET_MARKER_COLOR = "#B42E77";

export interface ProgressBarProps {
  /** Raw value; clamped to [0, max] (or [0, segments] in segmented mode). */
  value: number;
  /** Upper bound. Defaults to 100. Ignored in segmented mode (segments is the bound). */
  max?: number;
  /** Optional target value (same scale as value/max) drawn as a magenta marker line. */
  target?: number;
  /** Optional segment count for the discrete "N of segments" mode. */
  segments?: number;
  /** Accessible name for the progress meter. */
  label: string;
  className?: string;
}

/**
 * The navy progress bar from the Almikia indicator grammar. Always a solid
 * navy fill on a light track — never green, red or a gradient, even in the
 * segmented mode some mockups draw with a gradient (guide section 3 wins
 * over the mockup). Supports an optional magenta target marker and an
 * optional segmented ("3 of 4") rendering.
 */
export function ProgressBar({
  value,
  max = 100,
  target,
  segments,
  label,
  className,
}: ProgressBarProps) {
  const hasSegments = typeof segments === "number" && segments > 0;
  const effectiveMax = hasSegments ? (segments as number) : max;
  const { clamped, fraction } = clampToMax(value, effectiveMax);
  const percent = fraction * 100;

  const targetPercent =
    typeof target === "number"
      ? (clampToRange(target, 0, effectiveMax) / effectiveMax) * 100
      : null;

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={effectiveMax}
      data-testid="progress-bar"
      className={cn("relative w-full", className)}
    >
      {hasSegments ? (
        <div className="flex w-full gap-1">
          {Array.from({ length: segments as number }).map((_, index) => (
            <div
              key={index}
              data-testid="progress-bar-segment"
              data-filled={index < Math.round(clamped)}
              className="h-2 flex-1 rounded-full"
              style={{
                backgroundColor:
                  index < Math.round(clamped) ? NAVY_FILL : TRACK_COLOR,
              }}
            />
          ))}
        </div>
      ) : (
        <div
          className="relative h-2 w-full overflow-hidden rounded-full"
          style={{ backgroundColor: TRACK_COLOR }}
        >
          <div
            data-testid="progress-bar-fill"
            className="h-full rounded-full transition-[width]"
            style={{ width: `${percent}%`, backgroundColor: NAVY_FILL }}
          />
        </div>
      )}
      {targetPercent !== null ? (
        <div
          aria-hidden="true"
          data-testid="progress-bar-target"
          className="absolute top-0 h-2 w-0.5"
          style={{
            left: `${targetPercent}%`,
            backgroundColor: TARGET_MARKER_COLOR,
          }}
        />
      ) : null}
    </div>
  );
}
